import React, { useCallback, useEffect, useRef } from 'react';
import { youTubeId } from '../lib/videoUrl';

// YouTube refuses unmuted autoplay, so the iframe always starts muted and the
// mute state is applied afterwards through the IFrame API postMessage bridge —
// putting `mute` in the src instead would restart playback on every toggle.
export default function YouTubeEmbed({
  videoUrl,
  title,
  autoplay = true,
  muted = true,
  controls = false,
  loop = true,
  style,
}) {
  const frameRef = useRef(null);
  const videoId = youTubeId(videoUrl);

  const command = useCallback((func, args = []) => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(JSON.stringify({ event: 'command', func, args }), 'https://www.youtube.com');
  }, []);

  const applyMute = useCallback(() => {
    command(muted ? 'mute' : 'unMute');
    if (!muted) command('setVolume', [100]);
  }, [command, muted]);

  // The iframe may not have loaded when `muted` flips, so the state is also
  // re-applied from onLoad below.
  useEffect(() => {
    if (!videoId) return;
    applyMute();
  }, [applyMute, videoId]);

  if (!videoId) return null;

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    mute: '1',
    controls: controls ? '1' : '0',
    playsinline: '1',
    rel: '0',
    enablejsapi: '1',
  });
  // Looping a single video requires it to be its own one-item playlist.
  if (loop) {
    params.set('loop', '1');
    params.set('playlist', videoId);
  }

  return (
    <iframe
      ref={frameRef}
      title={title || 'YouTube video'}
      src={`https://www.youtube.com/embed/${videoId}?${params.toString()}`}
      frameBorder="0"
      allow="autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
      onLoad={applyMute}
      style={style}
    />
  );
}
